import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/common/DashboardBits";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Lock, KeyRound, FileCheck2, Fingerprint, ScrollText, Loader2, RefreshCw, UserX, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "-");

function DemoAccountsCard({ demo, onDisable, busy }) {
  if (!demo) return null;
  const risky = demo.active.length > 0 || demo.admin_default_password;
  return (
    <Card className={`border mb-6 ${risky ? "border-amber-200 bg-amber-50/40" : "border-emerald-200 bg-emerald-50/40"}`} data-testid="demo-accounts-card">
      <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className={`h-11 w-11 rounded-md ${risky ? "bg-amber-500" : "bg-emerald-600"} text-white flex items-center justify-center flex-shrink-0`}>
          {risky ? <AlertTriangle className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Demo accounts & default passwords</div>
          <div className="text-base font-semibold text-slate-900 mt-0.5" data-testid="demo-accounts-status">
            {demo.active.length ? `${demo.active.length} demo login(s) active` : "No demo logins active"} · seeding {demo.seed_enabled ? "ON (preview)" : "OFF"}
            {demo.admin_default_password ? " · admin still uses the default password" : ""}
          </div>
          {demo.active.length > 0 && <p className="text-xs text-slate-600 mt-1 break-words">{demo.active.join(", ")} — these bank/field/office logins are documented publicly and skip MFA. Disable them before going live.</p>}
        </div>
        {demo.active.length > 0 && (
          <Button variant="outline" onClick={onDisable} disabled={!!busy} className="border-amber-400 text-amber-900 hover:bg-amber-100" data-testid="disable-demo-button">
            {busy === "demo" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UserX className="h-4 w-4 mr-1" />} Disable demo accounts
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function Posture({ status }) {
  if (!status) return null;
  const items = [
    { icon: Fingerprint, label: "Two-step login (email OTP)", value: `${status.mfa.roles.join(", ")} · trust ${status.mfa.trust_days}d`, testId: "posture-mfa" },
    { icon: KeyRound, label: "Encryption at rest", value: `${status.encryption_at_rest.algorithm} · ${status.encryption_at_rest.executives_encrypted}/${status.encryption_at_rest.executives_total} exec records`, testId: "posture-encryption" },
    { icon: FileCheck2, label: "Upload scanning", value: `${status.uploads.allowed.join("/")} · ${status.uploads.max_mb} MB · ${status.uploads.clamav ? "ClamAV + built-in" : "built-in deep inspection"}`, testId: "posture-uploads" },
    { icon: Lock, label: "Brute-force lockout", value: `${status.login_rate_limit.max_attempts} tries / ${status.login_rate_limit.window_sec / 60} min · shared across replicas · API ${status.api_rate_limit_per_min}/min`, testId: "posture-ratelimit" },
    { icon: ShieldCheck, label: "CSRF, HSTS & security headers", value: "double-submit token · HTTPS redirect · CSP · pinned CORS", testId: "posture-headers" },
    { icon: ScrollText, label: "Tamper-evident audit log", value: `${status.audit_log_entries} chained entries`, testId: "posture-audit" },
    { icon: KeyRound, label: "Session revocation", value: "logout blocklist · all sessions reset on password change", testId: "posture-sessions" },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
      {items.map(({ icon: Icon, label, value, testId }) => (
        <Card key={label} className="border border-slate-200" data-testid={testId}>
          <CardContent className="p-4 flex items-start gap-3">
            <div className="h-9 w-9 rounded-md bg-emerald-600 text-white flex items-center justify-center flex-shrink-0"><Icon className="h-4 w-4" /></div>
            <div className="min-w-0">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</div>
              <div className="text-sm text-slate-800 mt-1 break-words">{value}</div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function SecurityPage() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState({ items: [], total: 0, actions: [] });
  const [action, setAction] = useState("all");
  const [verify, setVerify] = useState(null);
  const [busy, setBusy] = useState("");

  const loadLogs = useCallback((a = action) =>
    api.get("/audit-logs", { params: { limit: 100, ...(a !== "all" ? { action: a } : {}) } }).then((r) => setLogs(r.data)).catch((e) => toast.error(formatApiError(e))),
  [action]);
  const loadStatus = useCallback(() => api.get("/security/status").then((r) => setStatus(r.data)).catch((e) => toast.error(formatApiError(e))), []);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const runVerify = async () => {
    setBusy("verify");
    try { const { data } = await api.get("/audit-logs/verify"); setVerify(data); toast[data.ok ? "success" : "error"](data.ok ? `Chain intact — ${data.checked} entries verified` : `${data.problems.length} problem(s) found`); loadLogs(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const runEncrypt = async () => {
    setBusy("encrypt");
    try { const { data } = await api.post("/security/encrypt-pii"); toast.success(`${data.updated} record(s) encrypted`); loadStatus(); loadLogs(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const disableDemo = async () => {
    if (!window.confirm("Disable the seeded demo bank/field/office accounts? They will no longer be able to sign in.")) return;
    setBusy("demo");
    try { const { data } = await api.post("/security/demo-accounts/disable"); toast.success(data.disabled.length ? `Disabled: ${data.disabled.join(", ")}` : "No active demo accounts"); loadStatus(); loadLogs(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="security-page">
      <PageHeader
        title="Security"
        subtitle="Protection status and tamper-evident audit trail"
        right={
          <div className="flex gap-2">
            <Button variant="outline" onClick={runEncrypt} disabled={!!busy} data-testid="encrypt-pii-button">
              {busy === "encrypt" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <KeyRound className="h-4 w-4 mr-1" />} Encrypt legacy PII
            </Button>
            <Button onClick={runVerify} disabled={!!busy} className="bg-slate-950 hover:bg-slate-800" data-testid="verify-audit-button">
              {busy === "verify" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />} Verify audit chain
            </Button>
          </div>
        }
      />
      <DemoAccountsCard demo={status?.demo_accounts} onDisable={disableDemo} busy={busy} />
      <Posture status={status} />
      {verify && (
        <div className={`mb-6 rounded-md border px-4 py-3 text-sm ${verify.ok ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-red-200 bg-red-50 text-red-800"}`} data-testid="verify-result">
          {verify.ok ? `Audit chain intact — ${verify.checked} entries verified.` : `Integrity problems detected in ${verify.problems.length} entr${verify.problems.length === 1 ? "y" : "ies"}: ${verify.problems.slice(0, 3).map((p) => `#${p.seq} ${p.issue}`).join("; ")}`}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard label="Audit entries" value={logs.total} tone="blue" testId="audit-total" />
        <StatCard label="Distinct actions" value={logs.actions.length} tone="amber" testId="audit-actions" />
        <StatCard label="Chain status" value={verify ? (verify.ok ? "Intact" : "Broken") : "Not verified"} tone={verify ? (verify.ok ? "green" : "red") : "default"} testId="audit-chain-status" />
      </div>
      <div className="flex items-center gap-2 mb-4">
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-[260px]" data-testid="audit-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {logs.actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={() => loadLogs()} data-testid="audit-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>
      <Card className="border border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>#</TableHead><TableHead>Time</TableHead><TableHead>Action</TableHead><TableHead>Actor</TableHead><TableHead>Target</TableHead><TableHead>Details</TableHead><TableHead>IP</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {logs.items.map((e) => (
                <TableRow key={e.seq} data-testid={`audit-row-${e.seq}`}>
                  <TableCell className="num text-slate-500">{e.seq}</TableCell>
                  <TableCell className="text-xs text-slate-600 whitespace-nowrap">{fmt(e.ts)}</TableCell>
                  <TableCell><span className="inline-flex px-2 py-0.5 rounded-md bg-slate-100 text-slate-800 text-xs font-medium">{e.action}</span></TableCell>
                  <TableCell className="text-xs">{e.actor_email || "-"}{e.actor_role ? <span className="text-slate-400"> · {e.actor_role}</span> : null}</TableCell>
                  <TableCell className="text-xs font-mono text-slate-600 max-w-[160px] truncate">{e.target || "-"}</TableCell>
                  <TableCell className="text-xs text-slate-600 max-w-[320px] truncate">{Object.entries(e.details || {}).map(([k, v]) => `${k}=${Array.isArray(v) ? v.join("|") : v}`).join(" · ") || "-"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{e.ip || "-"}</TableCell>
                </TableRow>
              ))}
              {logs.items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">No audit entries yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
